import { FC, ReactElement, useState } from "react";
import {
  Button,
  Checkbox,
  Avatar,
  AvatarFallback,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import {
  formatNotificationType,
  getNotificationCategory,
  getNotificationText,
} from "@/utils/notificationTexts";
import {
  Notification,
  UsersNotificationSubscriptionStatus,
} from "@/models/notification/Notifications";
import { useNavigate } from "react-router-dom";
import {
  navigateToNotificationEntity,
  typesThatDoesntNavigate,
} from "@/services/notificationNavigationService";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "@/providers/notification/NotificationHubContext";
import { NotificationsWidgetConfig } from "@/models/dashboard/WidgetConfig";
import {
  BellRing,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import {
  groupNotificationsByDocumentAndDay,
  NotificationGroup,
} from "@/utils/notifications/notificationGrouping";
import { cn } from "@/lib/utils";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";

interface NotificationsWidgetProps {
  widgetId: string;
  name: string;
  config: NotificationsWidgetConfig;
}

interface WidgetNotificationGroupProps {
  group: NotificationGroup;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  checkedNotifications: string[];
  setCheckedNotifications: (ids: string[]) => void;
  isDismissingNotification: Record<string, boolean> | undefined;
  goToEntity: (notification: Notification, event?: React.MouseEvent) => void;
  dismissNotification: (id: string) => void;
  dismissNotifications: (ids: string[]) => void;
  formatDate: (dateString: string) => string;
  getNotificationText: (notification: Notification) => string;
  formatNotificationType: (type: string) => string;
}

function renderGroupRows(props: WidgetNotificationGroupProps): ReactElement[] {
  const {
    group,
    isOpen,
    onOpenChange,
    checkedNotifications,
    setCheckedNotifications,
    goToEntity,
    formatDate,
    getNotificationText,
    formatNotificationType,
  } = props;
  const ids = group.notifications.map((n) => n.id);
  const firstUpdatedAt = group.notifications[0]?.updatedAt ?? "";
  const checkedInGroup = ids.filter((id) => checkedNotifications.includes(id));
  const allInGroupChecked = checkedInGroup.length === ids.length;
  const someInGroupChecked = checkedInGroup.length > 0;
  const groupCheckboxChecked = allInGroupChecked
    ? true
    : someInGroupChecked
      ? "indeterminate"
      : false;

  const types = group.notifications.map((n) => n.type);
  const sameType = types.length > 0 && types.every((t) => t === types[0]);
  const categories = group.notifications.map((n) => getNotificationCategory(n));
  const sameCategory =
    categories.length > 0 && categories.every((c) => c === categories[0]);
  const groupTypeLabel = sameType
    ? formatNotificationType(types[0])
    : sameCategory
      ? categories[0]
      : "–";

  const actorNames = group.notifications.map((n) => n.actionCausedByName ?? "");
  const sameActor =
    actorNames.length > 0 && actorNames.every((a) => a === actorNames[0]);
  const groupActorName =
    sameActor && actorNames[0]?.trim() ? actorNames[0].trim() : null;

  if (group.notifications.length === 1) {
    const notification = group.notifications[0];
    return [
      <TableRow
        key={notification.id}
        onClick={(event) => goToEntity(notification, event)}
        className="cursor-pointer"
      >
        <TableCell className="w-8">
          <Checkbox
            className="cursor-pointer"
            checked={checkedNotifications.includes(notification.id)}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) => {
              setCheckedNotifications(
                checked
                  ? [...checkedNotifications, notification.id]
                  : checkedNotifications.filter((id) => id !== notification.id)
              );
            }}
          />
        </TableCell>
        <TableCell className="w-full max-w-0 min-w-[100px] overflow-hidden">
          <span
            className="truncate block"
            title={getNotificationText(notification)}
          >
            {getNotificationText(notification)}
          </span>
        </TableCell>
        <TableCell>
          <div
            className="truncate"
            title={formatNotificationType(notification.type)}
          >
            {formatNotificationType(notification.type)}
          </div>
        </TableCell>
        <TableCell className="w-12">
          <Avatar className="size-6 text-xs shrink-0">
            <AvatarFallback
              className="text-[10px]"
              title={notification.actionCausedByName}
            >
              {notification.actionCausedByName
                ?.split(" ")
                .map((name) => name.charAt(0).toUpperCase())
                .join("")}
            </AvatarFallback>
          </Avatar>
        </TableCell>
        <TableCell className="w-20">
          <div className="truncate" title={formatDate(notification.updatedAt)}>
            {formatDate(notification.updatedAt)}
          </div>
        </TableCell>
      </TableRow>,
    ];
  }

  const expandedRowClass = "bg-muted/40 border-l-2 border-l-primary/50";

  return [
    <TableRow
      key={`${group.groupKey}-trigger`}
      role="button"
      tabIndex={0}
      className={cn(
        "cursor-pointer hover:bg-accent/50 border-b",
        "focus-visible:outline-none focus-visible:ring-0",
        isOpen && expandedRowClass
      )}
      onClick={() => onOpenChange(!isOpen)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenChange(!isOpen);
        }
      }}
    >
      <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          className="cursor-pointer"
          checked={groupCheckboxChecked}
          onCheckedChange={() => {
            if (allInGroupChecked) {
              setCheckedNotifications(
                checkedNotifications.filter((id) => !ids.includes(id))
              );
            } else {
              setCheckedNotifications([
                ...new Set([...checkedNotifications, ...ids]),
              ]);
            }
          }}
        />
      </TableCell>
      <TableCell className="w-full max-w-0 min-w-[100px] overflow-hidden py-1.5">
        <span className="flex items-center gap-1.5 min-w-0">
          {isOpen ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate font-medium" title={group.label}>
            {group.label}
          </span>
          <span className="text-muted-foreground shrink-0">
            ({group.notifications.length})
          </span>
        </span>
      </TableCell>
      <TableCell>
        <span
          className={sameCategory ? undefined : "text-muted-foreground"}
          title={groupTypeLabel}
        >
          {groupTypeLabel}
        </span>
      </TableCell>
      <TableCell className="w-12">
        {groupActorName ? (
          <Avatar className="size-6 text-xs shrink-0">
            <AvatarFallback className="text-[10px]" title={groupActorName}>
              {groupActorName
                .split(" ")
                .map((name) => name.charAt(0).toUpperCase())
                .join("")}
            </AvatarFallback>
          </Avatar>
        ) : (
          <span className="text-muted-foreground">–</span>
        )}
      </TableCell>
      <TableCell className="w-20">
        <span
          className="truncate text-muted-foreground block"
          title={formatDate(firstUpdatedAt)}
        >
          {formatDate(firstUpdatedAt)}
        </span>
      </TableCell>
    </TableRow>,
    ...(isOpen
      ? group.notifications.map((notification) => (
          <TableRow
            key={notification.id}
            onClick={(event) => goToEntity(notification, event)}
            className={cn("cursor-pointer", expandedRowClass)}
          >
            <TableCell className="w-8">
              <Checkbox
                className="cursor-pointer"
                checked={checkedNotifications.includes(notification.id)}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={(checked) => {
                  setCheckedNotifications(
                    checked
                      ? [...checkedNotifications, notification.id]
                      : checkedNotifications.filter(
                          (id) => id !== notification.id
                        )
                  );
                }}
              />
            </TableCell>
            <TableCell className="w-full max-w-0 min-w-[100px] overflow-hidden">
              <span
                className="truncate block"
                title={getNotificationText(notification)}
              >
                {getNotificationText(notification)}
              </span>
            </TableCell>
            <TableCell>
              <div
                className="truncate"
                title={formatNotificationType(notification.type)}
              >
                {formatNotificationType(notification.type)}
              </div>
            </TableCell>
            <TableCell className="w-12">
              <Avatar className="size-6 text-xs shrink-0">
                <AvatarFallback
                  className="text-[10px]"
                  title={notification.actionCausedByName}
                >
                  {notification.actionCausedByName
                    ?.split(" ")
                    .map((name) => name.charAt(0).toUpperCase())
                    .join("")}
                </AvatarFallback>
              </Avatar>
            </TableCell>
            <TableCell className="w-20">
              <div
                className="truncate"
                title={formatDate(notification.updatedAt)}
              >
                {formatDate(notification.updatedAt)}
              </div>
            </TableCell>
          </TableRow>
        ))
      : []),
  ];
}
const NotificationsWidget: FC<NotificationsWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config: _config,
}) => {
  const {
    allNotifications,
    deleteNotifications,
    deleteAllNotifications,
    isDeletingNotifications,
    isDeletingAllNotifications,
    totalNotificationCount,
    dismissNotification,
    dismissNotifications,
    isDismissingNotification,
    isLoadingAllNotifications,
    isLoadingMoreAll,
    checkedNotifications,
    setCheckedNotifications,
    allNotificationsCurrentPage,
    allNotificationsTotalPages,
    goToAllNotificationsPage,
  } = useNotificationContext();
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(new Set());
  const [isDeleteSelectedDialogOpen, setIsDeleteSelectedDialogOpen] =
    useState(false);
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const goToEntity = async (
    notification: Notification,
    event?: React.MouseEvent
  ) => {
    const navigationPromise = navigateToNotificationEntity(
      notification,
      navigate,
      queryClient,
      event
    );

    if (
      (notification.status === UsersNotificationSubscriptionStatus.Active ||
        notification.status === UsersNotificationSubscriptionStatus.Seen) &&
      !typesThatDoesntNavigate.includes(notification.type)
    ) {
      dismissNotification(notification.id);
    }

    await navigationPromise;
  };

  const handleCheckAll = () => {
    if (checkedNotifications.length === allNotifications.length) {
      setCheckedNotifications([]);
    } else {
      setCheckedNotifications(
        allNotifications.map((notification) => notification.id)
      );
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("da-DK", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const setGroupOpen = (groupKey: string, open: boolean) => {
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      if (open) next.add(groupKey);
      else next.delete(groupKey);
      return next;
    });
  };

  const groups = groupNotificationsByDocumentAndDay(allNotifications);

  const handleConfirmDeleteSelected = async () => {
    await deleteNotifications(checkedNotifications);
    setIsDeleteSelectedDialogOpen(false);
  };

  const handleConfirmDeleteAll = async () => {
    await deleteAllNotifications();
    setIsDeleteAllDialogOpen(false);
  };

  // Initial loading state
  if (isLoadingAllNotifications) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  // Empty state
  if (allNotifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
        <BellRing className="h-8 w-8" />
        <p className="text-sm">Ingen notifikationer</p>
      </div>
    );
  }

  const pageSize = 20;
  const pageStart = (allNotificationsCurrentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(
    pageStart + allNotifications.length - 1,
    totalNotificationCount
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header with delete buttons */}
      <div className="px-2 py-1 border-b border-border flex justify-end gap-2 bg-muted/40">
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-7"
          disabled={
            checkedNotifications.length === 0 ||
            isDeletingNotifications ||
            isDeletingAllNotifications
          }
          onClick={() => setIsDeleteSelectedDialogOpen(true)}
        >
          {isDeletingNotifications
            ? "Sletter..."
            : `Slet ${checkedNotifications.length} valgte`}
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="text-xs h-7"
          disabled={
            totalNotificationCount === 0 ||
            isDeletingNotifications ||
            isDeletingAllNotifications
          }
          onClick={() => setIsDeleteAllDialogOpen(true)}
        >
          {isDeletingAllNotifications ? "Sletter..." : "Slet alle"}
        </Button>
      </div>

      {/* Scrollable table area */}
      <div className="themed-scrollbar flex-1 overflow-auto min-h-0 relative">
        <Table removeOverflowX={true} className="text-xs">
          <TableHeader className="sticky top-0 z-10 shadow-sm">
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  className="cursor-pointer"
                  checked={
                    allNotifications.length > 0 &&
                    checkedNotifications.length === allNotifications.length
                  }
                  onCheckedChange={handleCheckAll}
                />
              </TableHead>
              <TableHead className="text-muted-foreground font-semibold w-full max-w-0 min-w-[100px] overflow-hidden">
                Notifikation
              </TableHead>
              <TableHead className="text-muted-foreground font-semibold">
                Type
              </TableHead>
              <TableHead className="text-muted-foreground font-semibold w-12">
                Aktør
              </TableHead>
              <TableHead className="text-muted-foreground font-semibold w-20">
                Dato
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.flatMap((group) =>
              renderGroupRows({
                group,
                isOpen: openGroupKeys.has(group.groupKey),
                onOpenChange: (open) => setGroupOpen(group.groupKey, open),
                checkedNotifications,
                setCheckedNotifications,
                isDismissingNotification,
                goToEntity,
                dismissNotification,
                dismissNotifications,
                formatDate,
                getNotificationText,
                formatNotificationType,
              })
            )}
          </TableBody>
        </Table>

        {/* Page-change loading spinner overlay */}
        {isLoadingMoreAll && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/60 backdrop-blur-[1px]">
            <RefreshCw className="h-5 w-5 text-muted-foreground animate-spin" />
          </div>
        )}
      </div>

      {/* Sticky footer with count + pagination */}
      <div className="shrink-0 flex items-center justify-between border-t border-border px-3 py-1.5 text-xs text-muted-foreground bg-card">
        <span>
          {totalNotificationCount > 0
            ? `${pageStart}–${pageEnd} af ${totalNotificationCount}`
            : "0 notifikationer"}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={allNotificationsCurrentPage <= 1 || isLoadingMoreAll}
            onClick={() =>
              goToAllNotificationsPage(allNotificationsCurrentPage - 1)
            }
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-1">
            {allNotificationsCurrentPage} / {allNotificationsTotalPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={
              allNotificationsCurrentPage >= allNotificationsTotalPages ||
              isLoadingMoreAll
            }
            onClick={() =>
              goToAllNotificationsPage(allNotificationsCurrentPage + 1)
            }
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isDeleteSelectedDialogOpen}
        onClose={() => setIsDeleteSelectedDialogOpen(false)}
        onConfirm={handleConfirmDeleteSelected}
        confirmPending={isDeletingNotifications}
        title="Slet valgte notifikationer"
        description={`Er du sikker på, at du vil slette ${checkedNotifications.length} valgte notifikationer? Denne handling kan ikke fortrydes.`}
        confirmText="Slet valgte"
        variant="destructive"
      />
      <ConfirmationDialog
        isOpen={isDeleteAllDialogOpen}
        onClose={() => setIsDeleteAllDialogOpen(false)}
        onConfirm={handleConfirmDeleteAll}
        confirmPending={isDeletingAllNotifications}
        title="Slet alle notifikationer"
        description={`Er du sikker på, at du vil slette alle ${totalNotificationCount} notifikationer? Denne handling kan ikke fortrydes.`}
        confirmText="Slet alle"
        variant="destructive"
      />
    </div>
  );
};

export default NotificationsWidget;
